import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { SpinnerSize } from '../../../core/util/spinner/spinner-size.enum';
import { MatTableDataSource } from '@angular/material/table';
import { UserProfile } from '../../../core/user-profile/user-profile';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '../../../app-state';
import { WizardRunnerService } from '../../../core/wizard/wizard-runner.service';
import { BaseComponent } from '../../../core/base-component';
import { UserProfileService } from '../../../core/user-profile/user-profile.service';
import { debounceTime } from 'rxjs/operators';
import { Pageable } from '../../../core/service/pageable';
import { SetUsersList } from '../../administration-actions';
import { AdminState, selectAdminState } from '../../administration-state';
import { CreateUserWizard } from '../../../core/auth/create-user-wizard/create-user-wizard';
import { DatePipe } from '@angular/common';
import { InsightsTimestampPipe } from '../../../core/date/insights-timestamp.pipe';
import { HttpParams } from '@angular/common/http';

@Component({
  standalone: false,
  selector: 'mtn-users-administration',
  templateUrl: './users-administration.component.html',
  styleUrls: ['./users-administration.component.scss'],
  providers: [DatePipe, InsightsTimestampPipe]
})
export class UsersAdministrationComponent extends BaseComponent implements OnInit, AfterViewInit {

  readonly PAGE_SIZE = 25;

  currentPage: number = 0;
  dataSource = new MatTableDataSource<UserProfile>();
  displayedColumns: string[] = ['name', 'email', 'company', 'lastLoginDate'];
  filterForm = new UntypedFormGroup({
    filter: new UntypedFormControl()
  });
  isLoading = false;
  SpinnerSize = SpinnerSize;
  totalResults: number = 0;

  private currentFilter: string = null;

  @ViewChild(MatPaginator)
  paginator: MatPaginator;
  @ViewChild(MatSort)
  sort: MatSort;

  constructor(private activatedRoute: ActivatedRoute,
              private router: Router,
              private store: Store<AppState>,
              private userService: UserProfileService,
              private wizardRunner: WizardRunnerService) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToAdminState();
    this.subscribeToFilterChanges();
    this.loadUsers();
  }

  ngAfterViewInit() {
    this.configureTable();
  }

  navigateToUserDetails(user: UserProfile): void {
    this.router.navigate([user.uuid], {relativeTo: this.activatedRoute});
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.loadUsers();
  }

  openCreateUserWizard(): void {
    this.wizardRunner.run(new CreateUserWizard())
      .afterClosed().subscribe((result: CreateUserWizard) => {
      if (result.model.user) {
        this.navigateToUserDetails(result.model.user);
      }
    });
  }

  private configureTable(): void {
    this.sort.disableClear = true;
    this.dataSource.sort = this.sort;

    this.dataSource.sortingDataAccessor = (data: UserProfile, header: string): any => {
      if (header === 'name') {
        return data.name?.toLowerCase();
      } else if (header === 'email') {
        return data.email;
      } else if (header === 'company') {
        return data.company?.name;
      }

      return null;
    };
  }

  private loadUsers(query?: string): void {
    this.isLoading = true;
    this.dataSource.data = [];

    if (query !== undefined) {
      this.currentFilter = query;
    }

    let params = new HttpParams()
      .set('page', String(this.currentPage))
      .set('size', String(this.PAGE_SIZE));

    if (this.currentFilter) {
      params = params.set('q', this.currentFilter);
    }

    this.userService.findAll(params)
      .subscribe({
        next: (results: Pageable<UserProfile>) => {
          this.store.dispatch(SetUsersList({users: results}));
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  private subscribeToAdminState(): void {
    this.addSubscription(
      selectAdminState(this.store)
        .subscribe((state: AdminState) => {
          if (state.users) {
            this.dataSource.data = state.users.content;
            this.currentPage = state.users.number;
            this.totalResults = state.users.totalElements;
          }
        })
    );
  }

  private subscribeToFilterChanges(): void {
    this.addSubscription(
      this.filterForm.get('filter').valueChanges
        .pipe(debounceTime(300))
        .subscribe((value: string) => {
          this.currentPage = 0;
          this.loadUsers(value?.trim() || null);
        })
    );
  }

}
